import { useState, useRef, useEffect, useCallback, KeyboardEvent } from "react";
import { MapPin, X } from "lucide-react";
import { searchFrenchCities } from "../../lib/frenchCities";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";

interface MultiCityAutocompleteProps {
    values: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
}

export default function MultiCityAutocomplete({
    values,
    onChange,
    placeholder = "Ajouter une ville...",
}: MultiCityAutocompleteProps) {
    const [inputValue, setInputValue] = useState("");
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(isOpen);

    const updateSuggestions = useCallback((query: string) => {
        if (query.length < 1) { setSuggestions([]); setIsOpen(false); return; }
        const results = searchFrenchCities(query, 6).filter(c => !values.includes(c));
        setSuggestions(results);
        setIsOpen(results.length > 0);
        setActiveIndex(-1);
    }, [values]);

    const addCity = (city: string) => {
        if (!values.includes(city)) onChange([...values, city]);
        setInputValue("");
        setSuggestions([]);
        setIsOpen(false);
        setActiveIndex(-1);
        inputRef.current?.focus();
    };

    const removeCity = (city: string) => {
        onChange(values.filter(v => v !== city));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && suggestions[activeIndex]) {
                addCity(suggestions[activeIndex]);
            } else if (inputValue.trim()) {
                addCity(inputValue.trim());
            }
        } else if (e.key === "Escape") {
            setIsOpen(false);
        } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
            removeCity(values[values.length - 1]);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <div
                className="flex items-center gap-1 flex-wrap min-h-[32px] px-2 py-1 bg-surface border border-border rounded-md hover:border-border-strong focus-within:border-ink focus-within:ring-1 focus-within:ring-ink transition-colors duration-150 cursor-text"
                onClick={() => inputRef.current?.focus()}
            >
                <MapPin className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                {values.map(city => (
                    <span
                        key={city}
                        className="flex items-center gap-1 bg-surface-sunken text-ink-muted text-xs px-1.5 py-0.5 rounded-sm"
                    >
                        {city}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeCity(city); }}
                            className="text-ink-faint hover:text-ink-muted"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => { setInputValue(e.target.value); updateSuggestions(e.target.value); }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => inputValue.length >= 1 && updateSuggestions(inputValue)}
                    placeholder={values.length === 0 ? placeholder : ""}
                    autoComplete="off"
                    className="flex-1 min-w-[100px] text-xs outline-none bg-transparent"
                />
            </div>
            {isOpen && suggestions.length > 0 && (
                <div ref={dropdownAlign.ref} className={`absolute top-full mt-1 ${dropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-48 py-1 overflow-hidden`}>
                    {suggestions.map((city, index) => (
                        <button
                            key={city}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); addCity(city); }}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors duration-150 ${
                                index === activeIndex ? "bg-surface-sunken text-ink" : "text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            <MapPin className="w-3 h-3 shrink-0 text-ink-faint" />
                            {city}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
